import { useCallback, useEffect, useMemo, useRef } from "react";
import { io, Socket } from "socket.io-client";
import {
  SocketEvents,
  TClientToServerEvents,
  TServerToClientEvents,
  TSendMessageDTO,
  TMessageToUser,
  TSelectToUser,
  TErrorAI,
  TCheckFormDTO,
  TFormToUser,
  TSendFormDTO,
} from "@/models/chat.model";
import { BASE_URL } from "@/constants/common";

type TMeta = { socket: Socket };

type SocketEventsHandlers = {
  onConnect?: (meta: TMeta) => void;
  onDisconnect?: (meta: TMeta) => void;
  onMessageToUser?: (message: TMessageToUser, meta: TMeta) => void;
  onFormToUser?: (data: TFormToUser, meta: TMeta) => void;
  onSelectToUser?: (data: TSelectToUser, meta: TMeta) => void;
  onErrorAI?: (data: TErrorAI, meta: TMeta) => void;
  onError?: (error: string) => void;
};

export const useSocketIO = (params: SocketEventsHandlers) => {
  const socketRef = useRef<Socket<TServerToClientEvents, TClientToServerEvents> | null>(null);
  const eventsRef = useRef(params);

  useEffect(() => {
    eventsRef.current = params;
  }, [params]);

  useEffect(() => {
    if (socketRef.current) return;

    const socketInstance: Socket<TServerToClientEvents, TClientToServerEvents> = io(BASE_URL, {
      transports: ["websocket", "polling"],
      closeOnBeforeunload: true,
      tryAllTransports: true,
    });

    socketRef.current = socketInstance;

    socketInstance.on<SocketEvents.CONNECT>(SocketEvents.CONNECT, () => {
      eventsRef.current.onConnect?.({
        socket: socketInstance,
      });
    });

    socketInstance.on<SocketEvents.DISCONNECT>(SocketEvents.DISCONNECT, () => {
      eventsRef.current.onDisconnect?.({ socket: socketInstance });
    });

    socketInstance.on<SocketEvents.MESSAGE_TO_USER>(SocketEvents.MESSAGE_TO_USER, (data) => {
      eventsRef.current.onMessageToUser?.(data, { socket: socketInstance });
    });

    socketInstance.on<SocketEvents.FORM_TO_USER>(SocketEvents.FORM_TO_USER, (data) => {
      eventsRef.current.onFormToUser?.(data, { socket: socketInstance });
    });

    socketInstance.on<SocketEvents.SELECT_TO_USER>(SocketEvents.SELECT_TO_USER, (data) => {
      eventsRef.current.onSelectToUser?.(data, { socket: socketInstance });
    });

    socketInstance.on<SocketEvents.ERROR_AI>(SocketEvents.ERROR_AI, (data) => {
      eventsRef.current.onErrorAI?.(data, { socket: socketInstance });
    });

    socketInstance.on<SocketEvents.ERROR>(SocketEvents.ERROR, (error) => {
      console.error("Socket error:", error);
      eventsRef.current.onError?.(error);
    });

    return () => {
      socketInstance.removeAllListeners();
      socketInstance.disconnect();
      socketRef.current = null;
    };
  }, []);

  const handleSendMessageAI = useCallback((data: TSendMessageDTO) => {
    socketRef.current?.emit<SocketEvents.SEND_MESSAGE_AI>(SocketEvents.SEND_MESSAGE_AI, data);
  }, []);

  const handleSendFormAI = useCallback((data: TSendFormDTO) => {
    socketRef.current?.emit<SocketEvents.SEND_FORM_AI>(SocketEvents.SEND_FORM_AI, data);
  }, []);

  // Return to the conversation from the moment the user left
  const handleCheckForm = useCallback((data: TCheckFormDTO) => {
    socketRef.current?.emit<SocketEvents.CHECK_FORM>(SocketEvents.CHECK_FORM, data);
  }, []);

  const handleClearForm = useCallback((data: TCheckFormDTO) => {
    socketRef.current?.emit<SocketEvents.CLEAN_FORM>(SocketEvents.CLEAN_FORM, data);
  }, []);

  return useMemo(
      () => ({
        socket: socketRef.current,
        sendMessageAI: handleSendMessageAI,
        sendFormAI: handleSendFormAI,
        handleCheckForm,
        clearForm: handleClearForm,
      }),
      [handleSendMessageAI, handleSendFormAI, handleCheckForm, handleClearForm]
  );
};
